// OpenDB South African places reference — pure lookup logic.
//
// These functions operate on an in-memory `OpenDBPlace[]` and contain no
// browser/DOM dependencies, so they can be shared across frontend and backend.
// The dataset is loaded by the consumer (see the PWA's `loadPlaces`).
//
// Raw coordinates are never persisted or echoed by callers — only the place
// name and its type are surfaced.
// Finer place types rank higher so the displayed name is as specific as possible.
const PLACE_RANK = {
    suburb: 6,
    city: 5,
    town: 4,
    local_municipality: 3,
    metro_municipality: 2,
    district_municipality: 1,
    province: 0,
};
export const PLACE_LABEL = {
    suburb: 'Suburb',
    city: 'City',
    town: 'Town',
    local_municipality: 'Municipality',
    metro_municipality: 'Metro',
    district_municipality: 'District',
    province: 'Province',
};
const EARTH_RADIUS_KM = 6371;
function toRad(deg) {
    return (deg * Math.PI) / 180;
}
function haversineKm(aLat, aLon, bLat, bLon) {
    const dLat = toRad(bLat - aLat);
    const dLon = toRad(bLon - aLon);
    const lat1 = toRad(aLat);
    const lat2 = toRad(bLat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}
function normalize(s) {
    return (s ?? '').trim().toLowerCase();
}
/**
 * Pure forward lookup: fuzzy match a place name (or placeCode) to coordinates.
 * Skips records without valid coordinates or a name. Prefers finer place types
 * on ties. Returns null when nothing matches.
 */
export function nameToCoordsFrom(places, query) {
    const q = normalize(query);
    if (!q)
        return null;
    let best = null;
    for (const p of places) {
        if (p.latitude == null || p.longitude == null || !p.name)
            continue;
        let score = -1;
        if (normalize(p.placeCode) === q)
            score = 0; // exact placeCode
        else if (normalize(p.name) === q)
            score = 1; // exact name
        else if (normalize(p.name).startsWith(q))
            score = 2; // name prefix
        else if (normalize(p.name).includes(q))
            score = 3; // name substring
        else
            continue;
        if (!best ||
            score < best.score ||
            (score === best.score &&
                (PLACE_RANK[p.placeType] ?? 0) > (PLACE_RANK[best.p.placeType] ?? 0))) {
            best = { p, score };
        }
    }
    if (!best)
        return null;
    return {
        name: best.p.name,
        placeType: best.p.placeType,
        latitude: best.p.latitude,
        longitude: best.p.longitude,
    };
}
/**
 * Pure reverse lookup: nearest place to a coordinate via haversine, biasing
 * toward the finest place type within 1.5x of the nearest distance (so a
 * suburb/town is preferred over a municipality/province that is marginally closer).
 */
export function nearestPlaceFrom(places, lat, lon) {
    let best = null;
    for (const p of places) {
        if (p.latitude == null || p.longitude == null || !p.name)
            continue;
        const d = haversineKm(lat, lon, p.latitude, p.longitude);
        if (!best || d < best.d)
            best = { p, d };
    }
    if (!best)
        return null;
    const threshold = best.d * 1.5;
    let chosen = best;
    for (const p of places) {
        if (p.latitude == null || p.longitude == null || !p.name)
            continue;
        const d = haversineKm(lat, lon, p.latitude, p.longitude);
        if (d <= threshold) {
            const rank = PLACE_RANK[p.placeType] ?? 0;
            const chosenRank = PLACE_RANK[chosen.p.placeType] ?? 0;
            if (rank > chosenRank)
                chosen = { p, d };
        }
    }
    return {
        name: chosen.p.name,
        placeType: chosen.p.placeType,
        latitude: chosen.p.latitude,
        longitude: chosen.p.longitude,
        distanceKm: chosen.d,
    };
}
/**
 * Multi-match forward lookup for autocomplete UIs. Returns up to `limit`
 * places whose name/placeCode matches the query, ranked by match quality
 * then finer place type.
 */
export function searchPlacesFrom(places, query, limit = 12) {
    const q = normalize(query);
    if (!q)
        return [];
    const scored = [];
    for (const p of places) {
        if (p.latitude == null || p.longitude == null || !p.name)
            continue;
        let score = -1;
        const code = normalize(p.placeCode);
        const name = normalize(p.name);
        if (code === q)
            score = 0; // exact placeCode
        else if (name === q)
            score = 1; // exact name
        else if (name.startsWith(q))
            score = 2; // name prefix
        else if (name.includes(q))
            score = 3; // name substring
        else
            continue;
        scored.push({ p, score });
    }
    scored.sort((a, b) => a.score - b.score ||
        (PLACE_RANK[b.p.placeType] ?? 0) - (PLACE_RANK[a.p.placeType] ?? 0));
    return scored.slice(0, limit).map((s) => ({
        name: s.p.name,
        placeType: s.p.placeType,
        latitude: s.p.latitude,
        longitude: s.p.longitude,
    }));
}
/**
 * Resolve the province that contains a given place by walking the `parent`
 * chain up to the node whose `placeType` is `province`. Returns null when the
 * place has no parent chain reaching a province (e.g. a root-level entry).
 */
export function provinceOfFrom(places, place) {
    const byId = new Map();
    for (const p of places)
        byId.set(p.id, p);
    let current = place;
    const seen = new Set();
    while (current) {
        if (current.placeType === 'province' && current.name)
            return current.name;
        if (current.parent == null)
            return null;
        if (seen.has(current.id))
            return null; // cycle guard
        seen.add(current.id);
        current = byId.get(current.parent);
    }
    return null;
}
//# sourceMappingURL=places.js.map